import { useState } from 'react';
import { IconButton, Tab, Tabs, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TabHeader from '../../../../components/TabHeader';
import ConfirmDeleteDialog from '../../../../components/ConfirmDeleteDialog';
import type { SettingsOptionRow } from '../../types';
import OptionRow from './OptionRow';
import OptionModal from './OptionModal';
import { useManageOptions } from '../../hooks/useManageOptions';
import { useCategories } from '../../../planning/hooks/useCategories';
import { useNotification } from '../../../../context/useNotification';
import { OPTIONS_TAB_LABELS as LABELS } from '../../../../utils/labels';
import { hasFullAccess } from '../../../../utils/roles';
import type { MemberRole } from '../../../../types/roles';

interface OptionsTabProps {
  orgId: string | null;
  isSuperAdmin: boolean;
  role: MemberRole | null;
}

interface OptionModalState {
  mode: 'create' | 'edit';
  option?: SettingsOptionRow;
}

export default function OptionsTab({ orgId, isSuperAdmin, role }: OptionsTabProps) {
  const canEdit = hasFullAccess(role, isSuperAdmin);
  const {
    options,
    archivedOptions,
    loadArchived,
    addOption,
    updateOption,
    archiveOption,
    unarchiveOption,
    deleteOption,
  } = useManageOptions(orgId);
  const { categories } = useCategories(orgId);
  const { notifySuccess, notifyError } = useNotification();

  const [tab, setTab] = useState<'active' | 'archived'>('active');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [optionModal, setOptionModal] = useState<OptionModalState | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const handleTabChange = (_: React.SyntheticEvent, value: 'active' | 'archived') => {
    setTab(value);
    if (value === 'archived') loadArchived();
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteId) return;
    const id = pendingDeleteId;
    setPendingDeleteId(null);
    setDeleteError(null);
    const result = await deleteOption(id);
    if (result === 'ok') {
      notifySuccess(LABELS.deleteSuccess);
    } else if (result === 'has_machines') {
      setDeleteError(LABELS.deleteErrorHasMachines);
      notifyError(LABELS.deleteErrorHasMachines);
    } else {
      setDeleteError(LABELS.deleteError);
      notifyError(LABELS.deleteError);
    }
  };

  const handleSave = async (name: string, categoryIds: string[]): Promise<boolean> => {
    const ok = optionModal?.mode === 'create'
      ? await addOption(name, categoryIds)
      : optionModal?.option
        ? await updateOption(optionModal.option.id, name, categoryIds)
        : false;
    if (ok) notifySuccess(LABELS.saveSuccess); else notifyError(LABELS.mutationError);
    return ok;
  };

  const handleArchive = async (id: string) => {
    const ok = await archiveOption(id);
    if (ok) notifySuccess(LABELS.archiveSuccess); else notifyError(LABELS.mutationError);
  };

  const handleUnarchive = async (id: string) => {
    const ok = await unarchiveOption(id);
    if (ok) notifySuccess(LABELS.unarchiveSuccess); else notifyError(LABELS.mutationError);
  };

  const list = tab === 'active' ? options : archivedOptions;

  return (
    <div className="space-y-6">
      <TabHeader
        title={LABELS.title}
        description={LABELS.subtitle}
        addLabel={canEdit ? LABELS.newButton : undefined}
        onAdd={canEdit ? () => setOptionModal({ mode: 'create' }) : undefined}
      />

      {deleteError && (
        <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-2 flex items-center gap-2">
          <p className="text-sm text-red-700 flex-1">{deleteError}</p>
          <Tooltip title={LABELS.dismissError}>
            <IconButton size="small" onClick={() => setDeleteError(null)} aria-label={LABELS.dismissError}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      )}

      <div className="rounded-2xl border border-border overflow-hidden bg-surface">
        <Tabs value={tab} onChange={handleTabChange} className="border-b border-border px-2">
          <Tab label={LABELS.tabActive} value="active" />
          <Tab label={LABELS.tabArchived} value="archived" />
        </Tabs>

        {list.length === 0 ? (
          <p className="text-sm text-text-secondary text-center py-6">
            {tab === 'active' ? LABELS.noActive : LABELS.noArchived}
          </p>
        ) : (
          list.map((o, i) => (
            <OptionRow
              key={o.id}
              option={o}
              showDivider={i > 0}
              onEdit={canEdit ? () => setOptionModal({ mode: 'edit', option: o }) : undefined}
              onArchive={canEdit && !o.archived ? () => handleArchive(o.id) : undefined}
              onUnarchive={canEdit && o.archived ? () => handleUnarchive(o.id) : undefined}
              onDelete={canEdit ? () => setPendingDeleteId(o.id) : undefined}
            />
          ))
        )}
      </div>

      {optionModal && (
        <OptionModal
          option={optionModal.option}
          categories={categories}
          existingOptions={[...options, ...archivedOptions]}
          onClose={() => setOptionModal(null)}
          onSave={handleSave}
        />
      )}

      <ConfirmDeleteDialog
        open={!!pendingDeleteId}
        onClose={() => setPendingDeleteId(null)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}
